import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Link,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material'
import { generatePath, useNavigate, useParams } from 'react-router'
import { useEffect, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { AppRoutes } from '../../Routes'
import { TabDisplay } from '@oaisd/michdev.components.react'
import {
  GetAnnualPlanAndOrgName,
  GetAnnualPlanStatus,
  GetPlanOrganization,
  GetTasksForAnnualPlan
} from '../../../services/AnnualPlanService'
import LockIcon from '@mui/icons-material/Lock'
import { AnnualPlanDashboard } from './AnnualPlanDashboard'
import { PriorityActivitiesTab } from './priorityActivities/PriorityActivitiesTab'
import { AnnualPlanGettingStarted } from './AnnualPlanGettingStarted'
import { AnnualPlanStatus } from '../../../models/AnnualPlanStatus'
import { toast } from 'react-toastify'
import { BudgetOverviewTab } from './budget/BudgetOverviewTab'
import { useAnnualPlanUserRole } from '../../../hooks/use-annualPlanUserRole'
import { AnnualPlanRole } from '../../../models/AnnualPlanRole'
import { SubmitPlanTab } from './SubmitPlanTab'
import { useSearchParams } from 'react-router-dom'
import { SignContractTab } from './contract/SignContractTab'
import {
  PlanOrganizationTaskName,
  PlanOrganizationTaskStatus
} from '../../../models/PlanOrganizationTask'
import { RCNOperationsOverview } from './priorityActivities/rcnOperations/RCNOperationsOverview'
import {
  type AnnualPlanPhaseName,
  AnnualPlanPhaseSwitcher
} from '../implementation/AnnualPlanPhaseSwitcher'
import { handleApiResponse } from '../../../utils/apiHelpers'

export enum AnnualPlanTabs {
  GettingStarted = 0,
  Dashboard,
  RcnOperations,
  PriorityActivities,
  BudgetOverview,
  SubmitPlan,
  SignContract,
  ProgressReports
}

export function PlanningDetails (): JSX.Element {
  const nav = useNavigate()
  const { rcnId, planId, tab: tabString, planOrgId } = useParams()
  const { isLoading: userRoleLoading, error, userRole } = useAnnualPlanUserRole(planId != null ? Number(planId) : 0)
  const [searchParams] = useSearchParams()

  const tab = parseInt(tabString ?? '0')
  const showCommentsQuery = searchParams.get('comments')

  const [planName, setPlanName] = useState('')
  const [organizationName, setOrganizationName] = useState('')
  const [selectedTab, setSelectedTab] = useState<number>(AnnualPlanTabs.GettingStarted)
  const [isLoading, setIsLoading] = useState(true)
  const [annualPlanStatus, setAnnualPlanStatus] = useState<AnnualPlanStatus>(AnnualPlanStatus.NotStarted)
  const [showComments, setShowComments] = useState<boolean | undefined>()
  const [anyIncompleteTasks, setAnyIncompleteTasks] = useState(true)
  const [startContractHasBeenSigned, setStartContractHasBeenSigned] = useState(false)

  const hasStarted = annualPlanStatus !== AnnualPlanStatus.NotStarted
  const canSubmit = annualPlanStatus === AnnualPlanStatus.ReadyToSubmit && userRole === AnnualPlanRole.PlanAdministrator
  const canSignContract = (annualPlanStatus === AnnualPlanStatus.PlanAccepted || annualPlanStatus === AnnualPlanStatus.Finalized) &&
    (userRole === AnnualPlanRole.PlanReviewer ||
      (userRole === AnnualPlanRole.PlanAdministrator && startContractHasBeenSigned))

  const loadPlanStatus = async (): Promise<void> => {
    const status = await handleApiResponse(GetAnnualPlanStatus(Number(planId)))
    if (status != null) {
      setAnnualPlanStatus(status)
      if (status === AnnualPlanStatus.NotStarted) {
        updateTab(Number(AnnualPlanTabs.GettingStarted))
      }
    }
  }

  const loadTasks = async (): Promise<void> => {
    const tasks = await handleApiResponse(GetTasksForAnnualPlan(Number(planId)))
    if (tasks == null) return
    const incompleteTasks = tasks.some((task) =>
      task.taskName !== PlanOrganizationTaskName.BudgetOverview &&
      task.taskName !== PlanOrganizationTaskName.SignContract &&
      task.taskName !== PlanOrganizationTaskName.EPLITrainerInitialReview &&
      task.taskName !== PlanOrganizationTaskName.EPLITrainerFinalReview &&
      task.taskName !== PlanOrganizationTaskName.PeerToPeerDataCollectionSystem &&
      task.taskName !== PlanOrganizationTaskName.CoachingCapacityTool &&
      task.taskName !== PlanOrganizationTaskName.FocusStudentDataBOY &&
      task.taskName !== PlanOrganizationTaskName.FocusStudentDataEOY &&
      task.status !== PlanOrganizationTaskStatus.Accepted)
    setAnyIncompleteTasks(incompleteTasks)
    setStartContractHasBeenSigned(tasks.some((task) => task.taskName === PlanOrganizationTaskName.SignContract &&
      (task.status === PlanOrganizationTaskStatus.Submitted || task.status === PlanOrganizationTaskStatus.Accepted)))
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (planId == null || rcnId == null) return

      setIsLoading(true)

      const { success, errors, response } = await GetAnnualPlanAndOrgName(planId, rcnId)
      if (success) setPlanName(response.annualPlanName)
      else {
        for (const error of errors) {
          toast.error(error)
        }
      }

      // Use plan organization name if planOrgId is provided else use rcn organization name
      if (planOrgId == null) {
        setOrganizationName(response.organizationName)
      } else {
        const planOrganization = await handleApiResponse(GetPlanOrganization(Number(planOrgId)))
        if (planOrganization != null) {
          setOrganizationName(planOrganization.organization.name)
        }
      }

      await Promise.all([
        loadPlanStatus(),
        loadTasks()
      ])

      setIsLoading(false)
    }

    if (userRole != null) {
      void fetchData()
    }

    setSelectedTab(tab)
  }, [rcnId, planId, planOrgId, userRole])

  useEffect(() => {
    if (showCommentsQuery === 'true') {
      setShowComments(true)
    }
  }, [showCommentsQuery])

  const onPlanStart = (): void => {
    setSelectedTab(Number(AnnualPlanTabs.Dashboard))
    setAnnualPlanStatus(AnnualPlanStatus.Drafting)
  }

  const goBack = (): void => {
    const route = generatePath(AppRoutes.PLANNING, {})
    nav(route)
  }

  const updateTab = (tab: number, showComments?: boolean): void => {
    setSelectedTab(tab)
    setShowComments(showComments)

    if (rcnId == null || planId == null) return

    const route = generatePath(
      AppRoutes.PLANNING_DETAILS,
      { rcnId, planId, tab: tab.toString() }
    )
    nav(route + (showComments === true ? '?comments=true' : ''))
  }

  const onPlanSubmit = async (): Promise<void> => {
    await onPlanStatusUpdated()
    updateTab(AnnualPlanTabs.BudgetOverview)
  }

  const onPlanStatusUpdated = async (): Promise<void> => {
    await loadPlanStatus()
  }

  const getLockedLabel = (tabName: string): JSX.Element => {
    return <Box sx={{ display: 'flex' }}>
      <>{tabName}<LockIcon sx={{ marginLeft: '.5em', fontSize: '.875rem' }}/></>
    </Box>
  }

  const handlePhaseChange = (phase: AnnualPlanPhaseName): void => {
    if (phase === 'contract' || rcnId == null || planId == null) return

    const path = generatePath(
      AppRoutes.IMPLEMENTATION_DETAILS,
      { rcnId, planId, tab: String(selectedTab) }
    )
    nav(path)
  }

  if (!userRoleLoading && error != null) {
    return <Typography variant='body1' sx={{ textAlign: 'center' }}>{error}</Typography>
  }

  if (isLoading) {
    return <CircularProgress data-testid='loadingSpinner'/>
  }

  return <>
    <Breadcrumbs maxItems={2} aria-label="breadcrumb" sx={{ mb: '.5em' }}>
      <Link sx={{ cursor: 'pointer' }} underline="hover" color="inherit" onClick={goBack} data-testid='backToPlanning'>
        <ArrowBackIcon sx={{ verticalAlign: 'middle', mr: '5px' }} />
        Back to Planning
      </Link>
      <Link
        sx={{ cursor: 'pointer' }}
        underline="hover"
        color="inherit"
        onClick={() => { if (rcnId != null) nav(generatePath(AppRoutes.PLANNING_INSTANCE, { rcnId })) }}
      >
        {organizationName}
      </Link>
    </Breadcrumbs>

    <Stack direction='row' justifyContent='space-between'>
      <Stack spacing='.5em'>
        <Typography variant='h1'>{planName}</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='h4' data-testid='subPageHeader' mt='.5em'>{organizationName}</Typography>
        </Box>
      </Stack>

      <AnnualPlanPhaseSwitcher phase='contract' onPhaseChange={handlePhaseChange} />
    </Stack>

    <Tabs value={selectedTab} onChange={(e, val) => { updateTab(val) }} sx={{ mb: '2em', mt: '1em' }}>
      <Tab label='Getting Started' value={AnnualPlanTabs.GettingStarted} sx={{ textTransform: 'none' }} data-testid='gettingStartedTab' />
      <Tab disabled={!hasStarted} label={hasStarted ? 'Dashboard' : getLockedLabel('Dashboard')} value={AnnualPlanTabs.Dashboard} sx={{ textTransform: 'none' }} data-testid='dashboardTab' />
      <Tab disabled={!hasStarted} label={hasStarted ? 'RCN Operations' : getLockedLabel('RCN Operations')} value={AnnualPlanTabs.RcnOperations} sx={{ textTransform: 'none' }} data-testid='meetingTab' />
      <Tab disabled={!hasStarted} label={hasStarted ? 'Priority Activities' : getLockedLabel('Priority Activities')} value={AnnualPlanTabs.PriorityActivities} sx={{ textTransform: 'none' }} data-testid='activitiesTab' />
      <Tab disabled={!hasStarted} label={hasStarted ? 'Budget Overview' : getLockedLabel('Budget Overview')} value={AnnualPlanTabs.BudgetOverview} sx={{ textTransform: 'none' }} data-testid='budgetTab' />
      <Tab disabled={!hasStarted || !canSubmit || anyIncompleteTasks} label={hasStarted && canSubmit && !anyIncompleteTasks ? 'Submit Annual Plan' : getLockedLabel('Submit Annual Plan')} value={AnnualPlanTabs.SubmitPlan} sx={{ textTransform: 'none' }} data-testid='submitTab' />
      <Tab disabled={!hasStarted || !canSignContract || anyIncompleteTasks} label={hasStarted && canSignContract && !anyIncompleteTasks ? 'Sign Contract' : getLockedLabel('Sign Contract')} value={AnnualPlanTabs.SignContract} sx={{ textTransform: 'none' }} data-testid='contractTab' />
    </Tabs>

    <TabDisplay index={AnnualPlanTabs.GettingStarted} value={selectedTab}>
      <AnnualPlanGettingStarted
        annualPlanId={Number(planId)}
        annualPlanStatus={annualPlanStatus}
        rcnId={Number(rcnId)}
        onStartPlan={onPlanStart}
      />
    </TabDisplay>
    <TabDisplay index={AnnualPlanTabs.Dashboard} value={selectedTab}>
      {hasStarted &&
        <AnnualPlanDashboard annualPlanId={Number(planId)} rcnId={Number(rcnId)} changeTab={updateTab} />
      }
    </TabDisplay>
    <TabDisplay index={AnnualPlanTabs.RcnOperations} value={selectedTab}>
      {hasStarted &&
        <RCNOperationsOverview
          annualPlanId={Number(planId ?? 0)}
          rcnId={Number(rcnId)}
          showComments={showComments}
          onUpdateStatus={loadTasks}
        />
      }
    </TabDisplay>
    <TabDisplay index={AnnualPlanTabs.PriorityActivities} value={selectedTab}>
      {hasStarted &&
        <PriorityActivitiesTab
          annualPlanId={Number(planId)}
          rcnId={Number(rcnId)}
          onUpdateStatus={loadTasks}
        />
      }
    </TabDisplay>
    <TabDisplay index={AnnualPlanTabs.BudgetOverview} value={selectedTab}>
      {hasStarted &&
        <BudgetOverviewTab
          annualPlanId={Number(planId)}
          rcnId={Number(rcnId)}
          organizationName={organizationName}
          showComments={showComments}
          changeTab={updateTab}
          onUpdateStatus={onPlanStatusUpdated}
          implementationPhase={false}
        />
      }
    </TabDisplay>
    <TabDisplay index={AnnualPlanTabs.SubmitPlan} value={selectedTab}>
      {hasStarted && canSubmit && !anyIncompleteTasks &&
        <SubmitPlanTab annualPlanId={Number(planId)} rcnId={Number(rcnId)} onSubmit={onPlanSubmit} />
      }
    </TabDisplay>
    <TabDisplay index={AnnualPlanTabs.SignContract} value={selectedTab}>
      {hasStarted && canSignContract && !anyIncompleteTasks &&
        <SignContractTab
          annualPlanId={Number(planId)}
          rcnId={Number(rcnId)}
          organizationName={organizationName}
          showComments={showComments}
        />
      }
    </TabDisplay>
  </>
}
